import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import ActionButton from '@repo/ui/action-button'
import Button from '@repo/ui/button'
import DataTable, { Column, DataTableRef } from '@repo/ui/data-table'
import { DeleteIcon, FamilyTwoIcon, PlusIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useRef } from 'react'
import { useFormContext } from 'react-hook-form'

import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../lib/hooks/use-translate'
import { genderOneLetterMapping } from '../../../../lib/types/enums/common/gender.enum'
import {
  MaritalStatusEnum,
  maritalStatusEnumMapping
} from '../../../../lib/types/enums/staff-application/marital-status.enum'
import { EnrollmentChildInput } from '../../../../lib/types/models/enrollment/enrollment-child-input'
import { EnrollmentSiblingInput } from '../../../../lib/types/models/enrollment-siblings/enrollment-siblings-input'
import { IdInput } from '../../../../lib/types/utils'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedOption } from '../../../../lib/utils/functions/misc.functions'
import ActionTableWrapper from '../../../elements/action-table-wrapper'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import EnrollmentSiblingsForm from './enrollment-siblings-form'

const ClientEnrollmentChildFamilySection = () => {
  const form = useFormContext<EnrollmentChildInput>()
  const { t } = useTranslate()

  const siblings = form.watch('siblings') || []
  const ref = useRef<DataTableRef>(null)

  const { modalState, selectedData, closeModel, toggleModal } = useModalManager<
    'create' | 'delete',
    EnrollmentSiblingInput
  >(['create', 'delete'])

  const columns: Column<IdInput<EnrollmentSiblingInput>>[] = [
    {
      enableSorting: true,
      header: 'Prénom',
      accessorKey: 'name'
    },
    {
      enableSorting: true,
      header: 'Sexe',
      accessorKey: 'gender',
      cell: ({ row }) => genderOneLetterMapping[row.original.gender].label
    },
    {
      enableSorting: true,
      header: 'Âge',
      accessorKey: 'age'
    },

    {
      enableSorting: false,
      header: '',
      exportable: false,
      accessorKey: 'id',

      cell: ({ row }) => (
        <ActionButton
          variant={'danger'}
          className="mr-1"
          icon={DeleteIcon}
          onClick={() => toggleModal('delete', row.original)}
        />
      )
    }
  ]

  return (
    <InnerBox className="rounded-lg p-4 flex flex-col gap-4 ">
      <Typography variant="text" color={tailwindColors.grey.DEFAULT}>
        Famille
      </Typography>
      <FormField
        control={form.control}
        name="familySituation"
        render={({ field }) => (
          <FormItem className="w-full">
            <FormControl>
              <ControlledSelectInput<MaritalStatusEnum>
                options={Object.values(MaritalStatusEnum).map((status) => ({
                  label: t(maritalStatusEnumMapping[status]),
                  value: status
                }))}
                label={'Situation de famille des parents'}
                selectedOption={
                  field.value
                    ? getSelectedOption(field.value, t(maritalStatusEnumMapping[field.value]))
                    : undefined
                }
                onChange={(value) =>
                  form.setValue('familySituation', value, { shouldValidate: true })
                }
              />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="parentSocialSecurityNumber"
        render={({ field }) => (
          <FormItem className="w-full">
            <FormControl>
              <ControlledInput
                {...field}
                label={'Numéro de sécurité sociale du parent'}
                type="text"
              />
            </FormControl>
          </FormItem>
        )}
      />
      <ActionTableWrapper
        title={'Frères et soeurs'}
        transparent
        className="p-0"
        actions={[
          <Button
            key={'create'}
            type="button"
            icon={PlusIcon}
            label={'Ajouter un frère ou une sœur'}
            variant={'outline'}
            onClick={() => toggleModal('create')}
          />
        ]}
      >
        <DataTable<IdInput<EnrollmentSiblingInput>>
          ref={ref}
          manualSorting={false}
          columns={columns}
          data={siblings.map((sibling, index) => ({ ...sibling, id: index }))}
          rowCount={siblings.length}
          paginated={false}
        />
      </ActionTableWrapper>
      <Modal
        isOpen={modalState.create}
        title={'Ajouter un frère ou une sœur'}
        icon={FamilyTwoIcon}
        size="small"
        hideActions
        onCancel={() => closeModel('create')}
      >
        <EnrollmentSiblingsForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => toggleModal('create'),
            onConfirm: (data) => {
              form.setValue('siblings', [...siblings, data])
              toggleModal('create')
            }
          })}
        />
      </Modal>

      {selectedData && (
        <Modal
          actionButtonsOptions={{
            confirmText: 'Supprimer',
            cancelText: 'Fermer',
            onConfirm: () => {
              form.setValue(
                'siblings',
                siblings.filter((_, index) => index !== selectedData.id)
              )
              toggleModal('delete')
            }
          }}
          onCancel={() => toggleModal('delete')}
          isOpen={modalState.delete}
          title={`Supprimer le frère ou la sœur ${selectedData.name}`}
          icon={DeleteIcon}
          variant={'destructive'}
        />
      )}
    </InnerBox>
  )
}

export default ClientEnrollmentChildFamilySection
